import { useState, useEffect } from "react";
import { Send, MessageCircle, Phone } from "lucide-react";

const COURSES = [
  "Cálculo",
  "Física",
  "Probabilidades y Estadística",
  "Programación",
  "Otro",
];

const MODALITIES = [
  "Clases particulares",
  "Cursos asincrónicos",
  "Ambas / No estoy seguro",
];

export default function ContactSection({ selectedCourse, setSelectedCourse }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    course: "",
    modality: "",
    contactMethod: "email",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (selectedCourse) {
      setFormData((prev) => ({ ...prev, course: selectedCourse }));
    }
  }, [selectedCourse]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === "course") {
      setSelectedCourse(value);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Form doesn't submit yet — visual placeholder
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="py-20 lg:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        {/* Section header */}
        <div className="max-w-2xl mb-14">
          <p className="text-sm uppercase tracking-[0.2em] text-[#c77dff] font-semibold mb-4">
            Empieza hoy
          </p>
          <h2
            data-testid="contact-title"
            className="text-2xl md:text-3xl lg:text-4xl font-medium tracking-tight text-white mb-4"
          >
            Cuéntanos qué necesitas
          </h2>
          <p className="text-base text-[#a1a1aa] leading-relaxed">
            Completa el formulario y te contactaremos para orientarte según tu
            ramo y tu situación.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Form — takes 2/3 */}
          <div className="lg:col-span-2">
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="space-y-5"
            >
              {/* Name + Email row */}
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label
                    htmlFor="contact-name"
                    className="block text-sm font-medium text-[#a1a1aa] mb-2"
                  >
                    Nombre
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    data-testid="contact-input-name"
                    placeholder="Tu nombre"
                    className="w-full px-4 py-3 rounded-lg bg-[#242429] border border-white/5 text-white placeholder-[#52525b] text-sm focus:outline-none focus:border-[#9d4edd]/50 focus:ring-1 focus:ring-[#9d4edd]/30 transition-colors"
                  />
                </div>
                <div>
                  <label
                    htmlFor="contact-email"
                    className="block text-sm font-medium text-[#a1a1aa] mb-2"
                  >
                    Correo electrónico
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    data-testid="contact-input-email"
                    placeholder="tu@correo.com"
                    className="w-full px-4 py-3 rounded-lg bg-[#242429] border border-white/5 text-white placeholder-[#52525b] text-sm focus:outline-none focus:border-[#9d4edd]/50 focus:ring-1 focus:ring-[#9d4edd]/30 transition-colors"
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label
                  htmlFor="contact-phone"
                  className="block text-sm font-medium text-[#a1a1aa] mb-2"
                >
                  Teléfono
                </label>
                <input
                  id="contact-phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  data-testid="contact-input-phone"
                  placeholder="+56 9 1234 5678"
                  className="w-full px-4 py-3 rounded-lg bg-[#242429] border border-white/5 text-white placeholder-[#52525b] text-sm focus:outline-none focus:border-[#9d4edd]/50 focus:ring-1 focus:ring-[#9d4edd]/30 transition-colors"
                />
              </div>

              {/* Course + Modality row */}
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label
                    htmlFor="contact-course"
                    className="block text-sm font-medium text-[#a1a1aa] mb-2"
                  >
                    Curso o ramo
                  </label>
                  <select
                    id="contact-course"
                    value={formData.course}
                    onChange={(e) => handleChange("course", e.target.value)}
                    data-testid="contact-select-course"
                    className="w-full px-4 py-3 rounded-lg bg-[#242429] border border-white/5 text-white text-sm focus:outline-none focus:border-[#9d4edd]/50 focus:ring-1 focus:ring-[#9d4edd]/30 transition-colors appearance-none"
                  >
                    <option value="">Selecciona un curso</option>
                    {COURSES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="contact-modality"
                    className="block text-sm font-medium text-[#a1a1aa] mb-2"
                  >
                    Modalidad de interés
                  </label>
                  <select
                    id="contact-modality"
                    value={formData.modality}
                    onChange={(e) => handleChange("modality", e.target.value)}
                    data-testid="contact-select-modality"
                    className="w-full px-4 py-3 rounded-lg bg-[#242429] border border-white/5 text-white text-sm focus:outline-none focus:border-[#9d4edd]/50 focus:ring-1 focus:ring-[#9d4edd]/30 transition-colors appearance-none"
                  >
                    <option value="">Selecciona modalidad</option>
                    {MODALITIES.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Contact method */}
              <div>
                <label className="block text-sm font-medium text-[#a1a1aa] mb-3">
                  ¿Cómo prefieres que te contactemos?
                </label>
                <div className="flex gap-4">
                  <label
                    data-testid="contact-method-email"
                    className={`flex items-center gap-3 px-5 py-3 rounded-lg border cursor-pointer transition-colors ${
                      formData.contactMethod === "email"
                        ? "border-[#9d4edd]/40 bg-[#9d4edd]/5 text-white"
                        : "border-white/5 bg-[#242429] text-[#a1a1aa] hover:border-white/10"
                    }`}
                  >
                    <input
                      type="radio"
                      name="contactMethod"
                      value="email"
                      checked={formData.contactMethod === "email"}
                      onChange={(e) =>
                        handleChange("contactMethod", e.target.value)
                      }
                      className="sr-only"
                    />
                    <Send size={16} />
                    <span className="text-sm font-medium">Correo</span>
                  </label>
                  <label
                    data-testid="contact-method-whatsapp"
                    className={`flex items-center gap-3 px-5 py-3 rounded-lg border cursor-pointer transition-colors ${
                      formData.contactMethod === "whatsapp"
                        ? "border-[#9d4edd]/40 bg-[#9d4edd]/5 text-white"
                        : "border-white/5 bg-[#242429] text-[#a1a1aa] hover:border-white/10"
                    }`}
                  >
                    <input
                      type="radio"
                      name="contactMethod"
                      value="whatsapp"
                      checked={formData.contactMethod === "whatsapp"}
                      onChange={(e) =>
                        handleChange("contactMethod", e.target.value)
                      }
                      className="sr-only"
                    />
                    <MessageCircle size={16} />
                    <span className="text-sm font-medium">WhatsApp</span>
                  </label>
                </div>
              </div>

              {/* Message */}
              <div>
                <label
                  htmlFor="contact-message"
                  className="block text-sm font-medium text-[#a1a1aa] mb-2"
                >
                  Cuéntanos tu situación{" "}
                  <span className="text-[#52525b]">(opcional)</span>
                </label>
                <textarea
                  id="contact-message"
                  rows={4}
                  value={formData.message}
                  onChange={(e) => handleChange("message", e.target.value)}
                  data-testid="contact-input-message"
                  placeholder="¿En qué parte del ramo estás? ¿Tienes prueba pronto? Cuéntanos para orientarte mejor."
                  className="w-full px-4 py-3 rounded-lg bg-[#242429] border border-white/5 text-white placeholder-[#52525b] text-sm focus:outline-none focus:border-[#9d4edd]/50 focus:ring-1 focus:ring-[#9d4edd]/30 transition-colors resize-none"
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                data-testid="contact-submit-button"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-[#9d4edd] text-white font-medium text-sm hover:bg-[#7b2cbf] transition-colors duration-200"
              >
                {submitted ? "Mensaje enviado" : "Enviar mensaje"}
                <Send size={16} />
              </button>

              {submitted && (
                <p
                  data-testid="contact-success-message"
                  className="text-sm text-[#c77dff]"
                >
                  Gracias por contactarnos. Te responderemos pronto.
                </p>
              )}
            </form>
          </div>

          {/* WhatsApp sidebar — takes 1/3 */}
          <div className="lg:col-span-1">
            <div
              data-testid="whatsapp-cta"
              className="p-8 rounded-2xl bg-[#242429]/40 border border-white/5 sticky top-28"
            >
              <div className="p-3 rounded-xl bg-emerald-500/10 text-emerald-400 w-fit mb-5">
                <Phone size={22} />
              </div>
              <h3 className="text-lg font-medium text-white mb-3">
                ¿Tienes dudas?
              </h3>
              <p className="text-sm text-[#a1a1aa] leading-relaxed mb-6">
                Escríbenos directamente por WhatsApp y te respondemos lo antes
                posible. Sin compromiso.
              </p>
              <a
                href="https://wa.me/56912345678"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-link"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-emerald-600 text-white font-medium text-sm hover:bg-emerald-500 transition-colors duration-200 w-full justify-center"
              >
                <MessageCircle size={16} />
                Hablemos por WhatsApp
              </a>

              <div className="mt-8 pt-6 border-t border-white/5">
                <p className="text-xs text-[#71717a] mb-3 uppercase tracking-wider">
                  También puedes escribirnos a
                </p>
                <p className="text-sm text-[#a1a1aa]">
                  contacto@superate.cl
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
